import type { ExportListResponse } from '@/lib/api/reports-client.js';
import { cancelExport } from '@/lib/api/reports-client.js';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { EXPORTS_LIST_QUERY_KEY } from './use-exports-list.js';

export function useCancelExport() {
  const queryClient = useQueryClient();

  return useMutation<
    void,
    Error,
    string,
    { previousData: ExportListResponse | undefined; queryKey: unknown[] | undefined }
  >({
    mutationFn: (exportId: string) => cancelExport(exportId),

    onMutate: async (exportId) => {
      // Find the active exports-list query key to cancel and update optimistically
      const allKeys = queryClient.getQueriesData<ExportListResponse>({
        queryKey: [EXPORTS_LIST_QUERY_KEY],
      });

      let queryKey: unknown[] | undefined;
      let previousData: ExportListResponse | undefined;

      for (const [key, data] of allKeys) {
        if (data !== undefined) {
          queryKey = key as unknown[];
          previousData = data;
          break;
        }
      }

      if (queryKey !== undefined) {
        await queryClient.cancelQueries({ queryKey });

        queryClient.setQueryData<ExportListResponse>(queryKey, (old) => {
          if (old === undefined) return old;
          return {
            ...old,
            items: old.items.map((item) =>
              item.exportId === exportId ? { ...item, status: 'cancelled' as const } : item,
            ),
          };
        });
      }

      return { previousData, queryKey };
    },

    onError: (_err, _exportId, context) => {
      if (context?.queryKey !== undefined && context.previousData !== undefined) {
        queryClient.setQueryData(context.queryKey, context.previousData);
      }
    },

    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: [EXPORTS_LIST_QUERY_KEY] });
    },
  });
}
